<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 px-6 py-4">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Upsells</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage upsell offerings</p>
                                </div>
                                <div class="flex items-center gap-2">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                :disabled="isLoading"
                                                @click="loadData"
                                        >
                                                <svg class="w-4 h-4" :class="{ 'animate-spin': isLoading }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Refresh
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 px-4 py-2.5 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer hover:bg-orange-6"
                                                @click="showCreateModal = true"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                </svg>
                                                Create Upsell
                                        </button>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="p-6">
                        <!-- Search Card -->
                        <div class="bg-white rounded-xl shadow-sm px-6 py-4 mb-6">
                                <div class="flex flex-wrap gap-4 items-center">
                                        <div class="flex-1 min-w-48">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search upsell products..."
                                                                class="w-full py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <span class="text-xs bg-orange-2 text-orange-7 px-3 py-1 rounded-full font-medium">
                                                        {{ filteredUpsellOfferings.length }} upsell products
                                                </span>
                                        </div>
                                </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading upsells...</span>
                        </div>

                        <!-- Empty State -->
                        <div v-else-if="filteredUpsellOfferings.length === 0" class="bg-white rounded-xl shadow-sm py-16 px-4 text-center">
                                <svg class="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                <p class="text-gray-500 text-base m-0">No upsell products found</p>
                                <p class="text-gray-400 text-sm mt-2 mb-0">Create a new upsell to get started</p>
                        </div>

                        <!-- Card Gallery -->
                        <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                <div
                                        v-for="upsellOffering in filteredUpsellOfferings"
                                        :key="upsellOffering.id"
                                        class="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer group"
                                        @click="viewUpsellDetails(upsellOffering)"
                                >
                                        <!-- Thumbnail -->
                                        <div class="aspect-square bg-gray-100 relative overflow-hidden">
                                                <img
                                                        v-if="upsellOffering.thumbnail"
                                                        :src="upsellOffering.thumbnail"
                                                        :alt="upsellOffering.name"
                                                        class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                >
                                                <div v-else class="w-full h-full flex items-center justify-center">
                                                        <svg class="w-16 h-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                </div>
                                                <!-- Upsell count badge -->
                                                <div class="absolute top-3 right-3 bg-orange-7 text-white text-xs font-semibold px-2 py-1 rounded-full">
                                                        Linked to {{ upsellOffering.upsellCount }} offering{{ upsellOffering.upsellCount !== 1 ? 's' : '' }}
                                                </div>
                                        </div>

                                        <!-- Card Content -->
                                        <div class="p-4">
                                                <h3 class="text-base font-semibold text-gray-900 m-0 mb-1 truncate">
                                                        {{ upsellOffering.name || 'Unnamed Product' }}
                                                </h3>
                                                <p v-if="upsellOffering.variantname" class="text-sm text-gray-600 m-0 mb-1 truncate">
                                                        {{ upsellOffering.variantname }}
                                                </p>
                                                <p v-if="upsellOffering.size" class="text-xs text-gray-500 m-0 mb-2">
                                                        {{ upsellOffering.size }}
                                                </p>
                                                <p v-if="upsellOffering.description" class="text-sm text-gray-500 m-0 line-clamp-2">
                                                        {{ upsellOffering.description }}
                                                </p>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Detail Modal -->
                <div v-if="selectedUpsellOffering" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="selectedUpsellOffering = null">
                        <div class="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                                <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">{{ selectedUpsellOffering.name }}</h3>
                                        <button
                                                type="button"
                                                class="p-1.5 text-gray-400 bg-transparent border-none cursor-pointer rounded-md hover:text-gray-600"
                                                @click="selectedUpsellOffering = null"
                                        >
                                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6 overflow-y-auto flex-1">
                                        <!-- Offering Info -->
                                        <div class="flex gap-6 mb-6">
                                                <div class="w-32 h-32 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                                                        <img
                                                                v-if="selectedUpsellOffering.thumbnail"
                                                                :src="selectedUpsellOffering.thumbnail"
                                                                :alt="selectedUpsellOffering.name"
                                                                class="w-full h-full object-cover"
                                                        >
                                                        <div v-else class="w-full h-full flex items-center justify-center">
                                                                <svg class="w-12 h-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                        </div>
                                                </div>
                                                <div class="flex-1 min-w-0">
                                                        <p v-if="selectedUpsellOffering.variantname" class="text-sm text-gray-600 m-0 mb-1">{{ selectedUpsellOffering.variantname }}</p>
                                                        <p v-if="selectedUpsellOffering.size" class="text-sm text-gray-500 m-0 mb-2">{{ selectedUpsellOffering.size }}</p>
                                                        <p v-if="selectedUpsellOffering.description" class="text-sm text-gray-500 m-0">{{ selectedUpsellOffering.description }}</p>
                                                </div>
                                        </div>

                                        <!-- Upsell Records Table -->
                                        <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 m-0">Upsell Configurations ({{ getUpsellsForOffering(selectedUpsellOffering.id).length }})</h4>
                                        <div class="border border-gray-200 rounded-lg overflow-hidden">
                                                <table class="w-full border-collapse">
                                                        <thead>
                                                                <tr class="bg-gray-50">
                                                                        <th class="py-2.5 px-3 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">For Offering</th>
                                                                        <th class="py-2.5 px-3 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Description</th>
                                                                        <th class="py-2.5 px-3 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-20">Optional</th>
                                                                        <th class="py-2.5 px-3 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-20">Per Item</th>
                                                                        <th class="py-2.5 px-3 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-20">Auto</th>
                                                                        <th class="py-2.5 px-3 text-right text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-16">Actions</th>
                                                                </tr>
                                                        </thead>
                                                        <tbody>
                                                                <tr
                                                                        v-for="upsell in getUpsellsForOffering(selectedUpsellOffering.id)"
                                                                        :key="upsell.id"
                                                                        class="border-b border-gray-100 last:border-b-0"
                                                                >
                                                                        <td class="py-2.5 px-3 text-sm text-gray-900">{{ getOfferingName(upsell.offeringid) }}</td>
                                                                        <td class="py-2.5 px-3 text-sm text-gray-500">{{ upsell.description || '-' }}</td>
                                                                        <td class="py-2.5 px-3 text-center">
                                                                                <span :class="upsell.optional ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'" class="inline-block w-6 h-6 leading-6 rounded-full text-xs font-medium">
                                                                                        {{ upsell.optional ? '✓' : '✗' }}
                                                                                </span>
                                                                        </td>
                                                                        <td class="py-2.5 px-3 text-center">
                                                                                <span :class="upsell.peritem ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'" class="inline-block w-6 h-6 leading-6 rounded-full text-xs font-medium">
                                                                                        {{ upsell.peritem ? '✓' : '✗' }}
                                                                                </span>
                                                                        </td>
                                                                        <td class="py-2.5 px-3 text-center">
                                                                                <span :class="upsell.autoinclude ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'" class="inline-block w-6 h-6 leading-6 rounded-full text-xs font-medium">
                                                                                        {{ upsell.autoinclude ? '✓' : '✗' }}
                                                                                </span>
                                                                        </td>
                                                                        <td class="py-2.5 px-3 text-right">
                                                                                <button
                                                                                        type="button"
                                                                                        class="p-1.5 text-gray-400 bg-transparent border-none cursor-pointer rounded-md hover:text-red-600 hover:bg-red-50"
                                                                                        title="Delete"
                                                                                        @click.stop="confirmDelete(upsell)"
                                                                                >
                                                                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                        </svg>
                                                                                </button>
                                                                        </td>
                                                                </tr>
                                                        </tbody>
                                                </table>
                                        </div>
                                </div>
                                <div class="px-6 py-4 bg-gray-50 flex justify-end flex-shrink-0">
                                        <button
                                                type="button"
                                                class="py-2 px-4 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer hover:bg-gray-50"
                                                @click="selectedUpsellOffering = null"
                                        >
                                                Close
                                        </button>
                                </div>
                        </div>
                </div>

                <!-- Create Modal -->
                <div v-if="showCreateModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="showCreateModal = false">
                        <div class="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 overflow-hidden">
                                <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0">Create Upsell</h3>
                                        <button
                                                type="button"
                                                class="p-1.5 text-gray-400 bg-transparent border-none cursor-pointer rounded-md hover:text-gray-600"
                                                @click="showCreateModal = false"
                                        >
                                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                        </button>
                                </div>
                                <div class="p-6">
                                        <div class="flex flex-col gap-4">
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Upsell Offering *</label>
                                                        <select
                                                                v-model="newItem.upsellid"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                                <option :value="null" disabled>Select offering...</option>
                                                                <option v-for="offering in offerings" :key="offering.id" :value="offering.id">
                                                                        {{ offering.name }} {{ offering.variantname ? `- ${offering.variantname}` : '' }} {{ offering.size ? `(${offering.size})` : '' }}
                                                                </option>
                                                        </select>
                                                </div>
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1">For Offering *</label>
                                                        <select
                                                                v-model="newItem.offeringid"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                                <option :value="null" disabled>Select offering...</option>
                                                                <option v-for="offering in offerings" :key="offering.id" :value="offering.id">
                                                                        {{ offering.name }} {{ offering.variantname ? `- ${offering.variantname}` : '' }} {{ offering.size ? `(${offering.size})` : '' }}
                                                                </option>
                                                        </select>
                                                </div>
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                                        <input
                                                                v-model="newItem.description"
                                                                type="text"
                                                                placeholder="Enter description..."
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                        >
                                                </div>
                                                <div class="grid grid-cols-2 gap-4">
                                                        <div class="flex items-center gap-3">
                                                                <input
                                                                        id="optional"
                                                                        v-model="newItem.optional"
                                                                        type="checkbox"
                                                                        class="w-4 h-4 accent-orange-7"
                                                                >
                                                                <label for="optional" class="text-sm text-gray-700">Optional</label>
                                                        </div>
                                                        <div class="flex items-center gap-3">
                                                                <input
                                                                        id="peritem"
                                                                        v-model="newItem.peritem"
                                                                        type="checkbox"
                                                                        class="w-4 h-4 accent-orange-7"
                                                                >
                                                                <label for="peritem" class="text-sm text-gray-700">Per Item</label>
                                                        </div>
                                                        <div class="flex items-center gap-3">
                                                                <input
                                                                        id="autoinclude"
                                                                        v-model="newItem.autoinclude"
                                                                        type="checkbox"
                                                                        class="w-4 h-4 accent-orange-7"
                                                                >
                                                                <label for="autoinclude" class="text-sm text-gray-700">Auto Include</label>
                                                        </div>
                                                        <div class="flex items-center gap-3">
                                                                <input
                                                                        id="quantity"
                                                                        v-model="newItem.quantity"
                                                                        type="checkbox"
                                                                        class="w-4 h-4 accent-orange-7"
                                                                >
                                                                <label for="quantity" class="text-sm text-gray-700">Quantity Selectable</label>
                                                        </div>
                                                </div>
                                        </div>
                                </div>
                                <div class="px-6 py-4 bg-gray-50 flex justify-end gap-3">
                                        <button
                                                type="button"
                                                class="py-2 px-4 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer hover:bg-gray-50"
                                                @click="showCreateModal = false"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                type="button"
                                                class="py-2 px-4 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer hover:bg-orange-6 disabled:opacity-50"
                                                :disabled="!newItem.upsellid || !newItem.offeringid || isCreating"
                                                @click="createItem"
                                        >
                                                {{ isCreating ? 'Creating...' : 'Create' }}
                                        </button>
                                </div>
                        </div>
                </div>

                <!-- Toast Notification -->
                <div
                        v-if="toastMessage"
                        class="fixed bottom-4 right-4 py-3 px-4 rounded-lg shadow-lg text-white text-sm z-50"
                        :class="toastVariant === 'success' ? 'bg-green-600' : 'bg-red-600'"
                >
                        {{ toastMessage }}
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import Swal from 'sweetalert2';
import UpsellService from 'services/UpsellService';
import OfferingService from 'services/OfferingService';
import type { UpsellModel } from '@sosocio/creator/interfaces';

interface UpsellOffering {
        id: number;
        name: string;
        description: string;
        variantname: string;
        size: string;
        thumbnail: string;
        upsellCount: number;
}

export default defineComponent({
        name: 'UpsellsPage',
        setup() {
                const auth0 = useAuth0();
                
                const items = ref<UpsellModel[]>([]);
                const offerings = ref<any[]>([]);
                const isLoading = ref(true);
                const isCreating = ref(false);
                const searchQuery = ref('');
                const showCreateModal = ref(false);
                const selectedUpsellOffering = ref<UpsellOffering | null>(null);
                
                const toastMessage = ref('');
                const toastVariant = ref<'success' | 'error'>('success');
                let toastTimeout: ReturnType<typeof setTimeout>;
                
                const newItem = ref({
                        upsellid: null as number | null,
                        offeringid: null as number | null,
                        description: '',
                        optional: false,
                        peritem: false,
                        autoinclude: false,
                        quantity: false,
                });
                
                const showToast = (message: string, variant: 'success' | 'error' = 'success') => {
                        toastMessage.value = message;
                        toastVariant.value = variant;
                        clearTimeout(toastTimeout);
                        toastTimeout = setTimeout(() => {
                                toastMessage.value = '';
                        }, 3000);
                };
                
                const upsellOfferings = computed(() => {
                        const uniqueUpsellIds = [...new Set(items.value.map(item => item.upsellid))];
                        return uniqueUpsellIds.map(upsellId => {
                                const offering = offerings.value.find(o => o.id === upsellId);
                                const upsellCount = items.value.filter(item => item.upsellid === upsellId).length;
                                return {
                                        id: upsellId,
                                        name: offering?.name || `Offering #${upsellId}`,
                                        description: offering?.description || '',
                                        variantname: offering?.variantname || '',
                                        size: offering?.size || '',
                                        thumbnail: offering?.thumbnail || '',
                                        upsellCount,
                                } as UpsellOffering;
                        }).sort((a, b) => a.name.localeCompare(b.name));
                });
                
                const filteredUpsellOfferings = computed(() => {
                        if (!searchQuery.value.trim()) return upsellOfferings.value;
                        const query = searchQuery.value.toLowerCase();
                        return upsellOfferings.value.filter(offering => 
                                offering.name?.toLowerCase().includes(query) ||
                                offering.description?.toLowerCase().includes(query) ||
                                offering.variantname?.toLowerCase().includes(query) ||
                                offering.size?.toLowerCase().includes(query)
                        );
                });
                
                const getOfferingName = (offeringId: number) => {
                        const offering = offerings.value.find(o => o.id === offeringId);
                        if (!offering) return `Offering #${offeringId}`;
                        let name = offering.name || '';
                        if (offering.variantname) name += ` - ${offering.variantname}`;
                        if (offering.size) name += ` (${offering.size})`;
                        return name;
                };
                
                const getUpsellsForOffering = (upsellId: number) => {
                        return items.value.filter(item => item.upsellid === upsellId);
                };
                
                const viewUpsellDetails = (upsellOffering: UpsellOffering) => {
                        selectedUpsellOffering.value = upsellOffering;
                };
                
                const loadData = async () => {
                        isLoading.value = true;
                        try {
                                const [upsellsData, offeringsData] = await Promise.all([
                                        UpsellService.getAll(new URLSearchParams({ limit: '0' })),
                                        OfferingService.getAll(new URLSearchParams({ limit: '0' })),
                                ]);
                                items.value = upsellsData;
                                offerings.value = offeringsData;
                        } catch (error: any) {
                                console.error('Failed to load data:', error);
                                showToast('Failed to load upsells', 'error');
                        } finally {
                                isLoading.value = false;
                        }
                };
                
                const createItem = async () => {
                        if (!newItem.value.upsellid || !newItem.value.offeringid) return;
                        
                        isCreating.value = true;
                        try {
                                const created = await UpsellService.create({
                                        upsellid: newItem.value.upsellid,
                                        offeringid: newItem.value.offeringid,
                                        description: newItem.value.description,
                                        optional: newItem.value.optional ? 1 : 0,
                                        peritem: newItem.value.peritem ? 1 : 0,
                                        autoinclude: newItem.value.autoinclude ? 1 : 0,
                                        quantity: newItem.value.quantity ? 1 : 0,
                                } as any);
                                items.value.push(created);
                                showCreateModal.value = false;
                                newItem.value = {
                                        upsellid: null,
                                        offeringid: null,
                                        description: '',
                                        optional: false,
                                        peritem: false,
                                        autoinclude: false,
                                        quantity: false,
                                };
                                showToast('Upsell created successfully');
                        } catch (error: any) {
                                console.error('Failed to create upsell:', error);
                                showToast(error.message || 'Failed to create upsell', 'error');
                        } finally {
                                isCreating.value = false;
                        }
                };
                
                const confirmDelete = async (item: UpsellModel) => {
                        const result = await Swal.fire({
                                title: 'Delete Upsell?',
                                text: `Are you sure you want to delete this upsell?`,
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#dc2626',
                                confirmButtonText: 'Delete',
                        });
                        
                        if (result.isConfirmed) {
                                try {
                                        await UpsellService.delete(item.id);
                                        items.value = items.value.filter(i => i.id !== item.id);
                                        showToast('Upsell deleted successfully');
                                        
                                        if (selectedUpsellOffering.value) {
                                                const remainingUpsells = getUpsellsForOffering(selectedUpsellOffering.value.id);
                                                if (remainingUpsells.length === 0) {
                                                        selectedUpsellOffering.value = null;
                                                }
                                        }
                                } catch (error: any) {
                                        console.error('Failed to delete upsell:', error);
                                        showToast(error.message || 'Failed to delete upsell', 'error');
                                }
                        }
                };
                
                onMounted(() => {
                        if (auth0.isAuthenticated.value) {
                                loadData();
                        }
                });
                
                return {
                        items,
                        offerings,
                        isLoading,
                        isCreating,
                        searchQuery,
                        showCreateModal,
                        selectedUpsellOffering,
                        toastMessage,
                        toastVariant,
                        newItem,
                        upsellOfferings,
                        filteredUpsellOfferings,
                        getOfferingName,
                        getUpsellsForOffering,
                        viewUpsellDetails,
                        loadData,
                        createItem,
                        confirmDelete,
                };
        },
});
</script>
